import { decodeJWT } from '../../utils/auth/tokenUtils';

/**
 * Roles disponibles en el sistema
 */
export enum UserRole {
    DEMO_ADMIN = 'demo.admin',
    DEMO_USER = 'demo.user'
}

/**
 * Interface para información de roles del usuario
 */
export interface UserRoleInfo {
    roles: string[];
    hasRole: (role: UserRole | string) => boolean;
    hasAnyRole: (roles: (UserRole | string)[]) => boolean;
    hasAllRoles: (roles: (UserRole | string)[]) => boolean;
}

/**
 * Servicio para manejar roles y autorización basada en claims del token
 */
export class RoleService {
    /**
     * Extrae los roles de los claims del token ID
     */
    static extractRolesFromIdToken(idToken: string): string[] {
        try {
            console.log('Extracting roles from ID token...');
            
            // Usar la misma función de decodificación que usa el TokenDebugger
            const claims = decodeJWT(idToken);
            
            if (!claims) {
                console.warn('Failed to decode JWT token');
                return [];
            }
            
            console.log('ID Token claims (decoded with tokenUtils.decodeJWT):', claims);

            let roles: string[] = [];
            
            // En nuestra empresa solo usamos el claim "roles" que es un array
            const claimName = 'roles';
            
            if (claims[claimName]) {
                console.log(`Found roles in claim '${claimName}':`, claims[claimName]);
                if (Array.isArray(claims[claimName])) {
                    roles = [...claims[claimName]];
                    console.log(`Roles array from claim '${claimName}':`, roles);
                } else {
                    console.warn(`Claim '${claimName}' exists but is not an array:`, typeof claims[claimName]);
                }
            } else {
                console.warn(`Claim '${claimName}' not found in token`);
                // Mostrar todos los claims disponibles para debugging
                console.log('Available claims:', Object.keys(claims));
            }

            // Remover duplicados y filtrar valores vacíos
            const uniqueRoles = Array.from(new Set(roles));
            const filteredRoles = uniqueRoles.filter(role => role && role.trim() !== '');
            
            console.log('Final extracted roles from ID token:', filteredRoles);
            return filteredRoles;

        } catch (error) {
            console.error('Error extracting roles from ID token:', error);
            return [];
        }
    }
    
    /**
     * Decodifica una cadena base64url
     */
    private static base64UrlDecode(str: string): string {
        // Convertir base64url a base64
        let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
        
        // Agregar padding si es necesario
        while (base64.length % 4) {
            base64 += '=';
        }

        // Decodificar base64
        return atob(base64);
    }

    /**
     * Crea un objeto UserRoleInfo con métodos de utilidad
     */
    static createUserRoleInfo(roles: string[]): UserRoleInfo {
        return {
            roles,
            hasRole: (role: UserRole | string) => roles.includes(role),
            hasAnyRole: (requiredRoles: (UserRole | string)[]) => 
                requiredRoles.some(role => roles.includes(role)),
            hasAllRoles: (requiredRoles: (UserRole | string)[]) => 
                requiredRoles.every(role => roles.includes(role))
        };
    }

    /**
     * Verifica si un usuario tiene un rol específico
     */
    static hasRole(userRoles: string[], requiredRole: UserRole | string): boolean {
        return userRoles.includes(requiredRole);
    }

    /**
     * Verifica si un usuario tiene al menos uno de los roles requeridos
     */
    static hasAnyRole(userRoles: string[], requiredRoles: (UserRole | string)[]): boolean {
        return requiredRoles.some(role => userRoles.includes(role));
    }

    /**
     * Verifica si un usuario tiene todos los roles requeridos
     */
    static hasAllRoles(userRoles: string[], requiredRoles: (UserRole | string)[]): boolean {
        return requiredRoles.every(role => userRoles.includes(role));
    }

    /**
     * Obtiene una descripción legible del rol
     */
    static getRoleDescription(role: UserRole | string): string {
        const descriptions: Record<string, string> = {
            [UserRole.DEMO_ADMIN]: 'Demo Administrator - Full access to demo features',
            [UserRole.DEMO_USER]: 'Demo User - Limited access to demo features'
        };

        return descriptions[role] || `Custom Role: ${role}`;
    }

    /**
     * Obtiene todos los roles disponibles en el sistema
     */
    static getAllAvailableRoles(): UserRole[] {
        return Object.values(UserRole);
    }
}